import React from "react";
import { Link } from "react-router-dom";
import Seo from "./components/Seo";
import YouTubeEmbed from "./components/YouTubeEmbed"; // ⬅️ new import

function HomePage() {
    return (
        <div className="home-page">
            {/* SEO / Meta tags */}
            <Seo
                title="NYX NoCode — Create With AI in Minutes"
                description="Turn any classroom into a creative lab. NYX NoCode lets students build pages, media, and interactive experiences with AI—fast, safe, and teacher-led."
            />

            {/* NYX NoCode Logo/Hero */}
            <section className="features-section">
                <h2>NYX NoCode Helps Teachers Instruct Their Students in AI</h2>
                <div className="video-container">
                    <YouTubeEmbed
                        playlistId="PLjQ8Tc7k38HmOysh4ZUa8DHAM53q-S4gt"
                        firstVideoId="7LFYhEEVbGE"
                        title="NYX NoCode Demo"
                    />
                </div>
            </section>

            {/* About NYX NoCode */}
            <section className="about-section">
                <h2>About NYX NoCode</h2>
                <div className="about-content">
                    <p>
                        <strong>
                            NYX NoCode is an educator-led platform that transforms classrooms
                            into creative studios
                        </strong>
                        , giving students hands-on experience with generative AI and modern
                        computing without requiring prior coding skills. Named after Nyx,
                        the Greek goddess of the night, the platform was built to make
                        cutting-edge AI accessible, ethical, and empowering for all
                        learners—especially in schools where resources are limited.
                    </p>

                    <div className="about-highlights">
                        <h3>What Students Create:</h3>
                        <ul>
                            <li>Working web pages and interactive media</li>
                            <li>3D designs and visual projects</li>
                            <li>Speech and video projects</li>
                            <li>Multi-step applications that can be shared with peers</li>
                        </ul>

                        <p>
                            <strong>The platform is designed to be intuitive:</strong> doodles
                            become prompts, prompts become webpages, and webpages evolve into
                            full projects.
                        </p>
                    </div>

                    <div className="about-highlights">
                        <h3>Flexible Program Options:</h3>
                        <ul>
                            <li>
                                <strong>Demo Day</strong> - Perfect for trying
                                NYX NoCode and generative AI.
                            </li>
                            <li>
                                <strong>3-week NYX NoCode Hackathon</strong> -
                               Transformative experience that teaches students how to think about AI.
                            </li>
                            <li>
                                <strong>Permanent classroom integration</strong> - Low annual
                                fee daily use of NYX NoCode.
                            </li>
                        </ul>
                    </div>

                    <div className="about-highlights">
                        <h3>Proven Impact:</h3>
                        <ul>
                            <li>
                                <strong>Hundreds of students</strong> reached across Arkansas
                                and Georgia
                            </li>
                            <li>
                                <strong>NPR coverage</strong> of our school hackathons
                            </li>
                            <li>
                                Students gain confidence as{" "}
                                <strong>creators, not just consumers</strong> of technology
                            </li>
                            <li>
                                <strong>Hours of prep time saved</strong> for teachers while
                                igniting student engagement
                            </li>
                        </ul>
                    </div>

                    <div className="about-highlights">
                        <h3>Built for Every Student:</h3>
                        <ul>
                            <li>
                                <strong>Works on any device</strong> - no advanced hardware or
                                technical background required
                            </li>
                            <li>
                                <strong>Supports diverse learning styles</strong> with multiple
                                ways to engage and create
                            </li>
                            <li>
                                <strong>Gives every student</strong> the tools to participate in
                                the digital economy
                            </li>
                        </ul>
                    </div>

                    <p className="about-mission">
                        <strong>
                            Backed by Microsoft for Startups, Builders + Backers, Techstars and AWS
                        </strong>
                        , and led by award-winning AI innovators, NYX NoCode is building a
                        new standard for AI education:{" "}
                        <strong>safe, teacher-first, creative, and equitable</strong>. Our
                        mission is simple—turn students into future-ready creators who
                        understand not just how to use AI, but how to use it to shape their own destiny.
                    </p>
                </div>
            </section>

            {/* What Makes NYX NoCode Unique */}
            <section className="features-section">
                <h2>What Makes NYX NoCode Unique</h2>
                <div className="features-list">
                    <div className="feature-item">
                        <span className="feature-icon">✨</span>
                        <div className="feature-content">
                            <strong>Prompt-Based Interface</strong> – Students build using
                            natural language, learning to structure requests and translate
                            ideas into working code.
                        </div>
                    </div>
                    <div className="feature-item">
                        <span className="feature-icon">✏️</span>
                        <div className="feature-content">
                            <strong>Doodle-to-Prompt Creativity</strong> – Simple doodles turn
                            to prompts, apps, images and videos.
                        </div>
                    </div>
                    <div className="feature-item">
                        <span className="feature-icon">🎨</span>
                        <div className="feature-content">
                            <strong>Intuitive Visual Design</strong> – Clean, responsive
                            layouts make every project customizable and mobile-ready.
                        </div>
                    </div>
                    <div className="feature-item">
                        <span className="feature-icon">💡</span>
                        <div className="feature-content">
                            <strong>Prompt Library</strong> – A growing prompt library helps
                            students remix, adapt, and invent new projects.
                        </div>
                    </div>
                    <div className="feature-item">
                        <span className="feature-icon">🔍</span>
                        <div className="feature-content">
                            <strong>Reverse Engineering Mode</strong> – Students learn coding
                            through modifying their AI generated projects.
                        </div>
                    </div>
                    <div className="feature-item">
                        <span className="feature-icon">🌎</span>
                        <div className="feature-content">
                            <strong>Beyond Language Barriers</strong> – Breaks spoken language
                            boundaries, letting students generate and learn across many
                            languages.
                        </div>
                    </div>
                    <div className="feature-item">
                        <span className="feature-icon">🚀</span>
                        <div className="feature-content">
                            <strong>No-Code, Real Results</strong> – From portfolios to
                            storytelling apps to business prototypes, projects look and feel
                            like they belong in the real world.
                        </div>
                    </div>
                </div>
            </section>

            {/* Get Started CTA */}
            <section className="get-started-section">
                <h2>Ready to Transform Your Classroom?</h2>
                <p>
                    Join educators who are using NYX NoCode to empower their students with
                    AI creativity. Whether you're looking for a NYX NoCode Hackathon
                    experience or ongoing classroom access, we're here to help you get
                    started.
                </p>
                <Link to="/get-started" className="cta-button">
                    🚀 Get Started Today
                </Link>
            </section>
        </div>
    );
}

export default HomePage;